import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Pencil,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Info,
  Plus,
  Clock,
  MapPin,
  Phone,
  Tag,
} from "lucide-react";
import type { Restaurant } from "../../Manager/restaurant.types";
import { submitSuggestion } from "../../../lib/api/suggest.apis";
import { useAuth } from "../../../Context/AuthContext";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SuggestEditProps {
  restaurant: Restaurant;
  onClose: () => void;
}

interface FieldChange {
  field: string;
  suggestedValue: unknown;
}

interface EditSection {
  id: string;
  label: string;
  icon: ReactNode;
  fields: EditField[];
}

interface EditField {
  key: string;       // dot-notation path
  label: string;
  type: "text" | "textarea" | "array" | "hours" | "select";
  placeholder?: string;
  options?: string[];
  hint?: string;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

const BUDGET_OPTIONS = ["$", "$$", "$$$", "$$$$"];

const CUISINE_SUGGESTIONS = [
  "African", "American", "Arabian", "Asian", "BBQ", "Breakfast", "Burgers",
  "Chinese", "Ethiopian", "Fast Food", "French", "Greek", "Indian", "Italian",
  "Japanese", "Kenyan", "Korean", "Mediterranean", "Mexican", "Middle Eastern",
  "Nyama Choma", "Pizza", "Seafood", "Steakhouse", "Sushi", "Thai", "Vegan",
];

const SECTIONS: EditSection[] = [
  {
    id: "basic",
    label: "Basic Info",
    icon: <Pencil className="w-4 h-4" />,
    fields: [
      { key: "name", label: "Restaurant Name", type: "text", placeholder: "e.g. The Carnivore" },
      { key: "description", label: "Description", type: "textarea", placeholder: "What makes this place special?", hint: "Keep it honest and helpful for other diners." },
    ],
  },
  {
    id: "location",
    label: "Location",
    icon: <MapPin className="w-4 h-4" />,
    fields: [
      { key: "location.address", label: "Street Address", type: "text", placeholder: "e.g. Langata Road" },
      { key: "location.area", label: "Area / Neighbourhood", type: "text", placeholder: "e.g. Karen" },
      { key: "location.city", label: "City", type: "text", placeholder: "e.g. Nairobi" },
    ],
  },
  {
    id: "contact",
    label: "Contact",
    icon: <Phone className="w-4 h-4" />,
    fields: [
      { key: "contactInfo.phone", label: "Phone Number", type: "text", placeholder: "+254 7XX XXX XXX" },
      { key: "contactInfo.email", label: "Email", type: "text", placeholder: "hello@restaurant.co.ke" },
      { key: "contactInfo.website", label: "Website", type: "text", placeholder: "https://restaurant.co.ke" },
    ],
  },
  {
    id: "hours",
    label: "Opening Hours",
    icon: <Clock className="w-4 h-4" />,
    fields: DAYS.map((day) => ({
      key: `openingHours.${day}`,
      label: day.charAt(0).toUpperCase() + day.slice(1),
      type: "text" as const,
      placeholder: "e.g. 09:00 - 22:00 or Closed",
    })),
  },
  {
    id: "tags",
    label: "Cuisine & Tags",
    icon: <Tag className="w-4 h-4" />,
    fields: [
      { key: "cuisine", label: "Cuisine Types", type: "array", placeholder: "Add cuisine type…" },
      { key: "tags", label: "Features & Tags", type: "array", placeholder: "e.g. Rooftop seating…" },
      { key: "experience", label: "Dining Experience", type: "array", placeholder: "e.g. Fine dining…" },
      { key: "budgetLevel", label: "Price Range", type: "select", options: BUDGET_OPTIONS },
    ],
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

// ─── Field Components ──────────────────────────────────────────────────────────

const inputClass =
  "w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:border-gray-400 focus:outline-none placeholder-gray-400 text-gray-900 transition-all";

function ArrayField({
  value,
  onChange,
  placeholder,
  suggestions,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
}) {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filtered = suggestions?.filter(
    (s) => s.toLowerCase().includes(input.toLowerCase()) && !value.includes(s)
  ) ?? [];

  const add = (v: string) => {
    const trimmed = v.trim();
    if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
    setInput("");
    setShowSuggestions(false);
  };

  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {/* Tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-900 text-white text-xs font-medium"
            >
              {item}
              <button onClick={() => remove(i)} className="hover:opacity-70 transition-opacity ml-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder={placeholder}
          className={inputClass}
        />
        {input && (
          <button
            onClick={() => add(input)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
        {/* Suggestions dropdown */}
        <AnimatePresence>
          {showSuggestions && filtered.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-40 overflow-y-auto"
            >
              {filtered.slice(0, 8).map((s) => (
                <button
                  key={s}
                  onMouseDown={() => add(s)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {s}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function SuggestEditModal({ restaurant, onClose }: SuggestEditProps) {
  const [activeSection, setActiveSection] = useState(0);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [step, setStep] = useState<"edit" | "confirm" | "success">("edit");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const { user, openAuthModal } = useAuth();

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Seed initial values from restaurant data
  useEffect(() => {
    const initial: Record<string, unknown> = {};
    SECTIONS.forEach((section) =>
      section.fields.forEach(({ key }) => {
        initial[key] = getNestedValue(restaurant as unknown as Record<string, unknown>, key) ?? "";
      })
    );
    setValues(initial);
  }, [restaurant]);

  const setField = (key: string, val: unknown) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setTouched((prev) => new Set([...prev, key]));
  };

  // Build changed fields only
  const getChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    touched.forEach((key) => {
      const original = getNestedValue(restaurant as unknown as Record<string, unknown>, key);
      const current = values[key];
      const isDifferent = JSON.stringify(original) !== JSON.stringify(current);
      if (isDifferent) changes.push({ field: key, suggestedValue: current });
    });
    return changes;
  };

  const changedFields = getChanges();
  const hasChanges = changedFields.length > 0;

  const currentSection = SECTIONS[activeSection];

  async function handleSubmit() {
    if (!hasChanges) return;
    if (!user) {
      openAuthModal();
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitSuggestion(restaurant._id,  JSON.stringify({
          changes: changedFields,
          reason: reason.trim() || undefined,
        }))

      if (!res.success === true) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Submission failed. Please try again.");
      }

      setStep("success");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  const fieldLabel = (key: string) =>
    SECTIONS.flatMap((s) => s.fields).find((f) => f.key === key)?.label ?? key;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-[2px]"
    >
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className="relative bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "92dvh" }}
      >
        {/* ── Drag handle (mobile) ───────────────────────────────────────── */}
        <div className="flex justify-center pt-3 sm:hidden shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-6 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Pencil className="w-4 h-4 text-gray-400" />
              <h2 className="text-base font-semibold text-gray-900">Suggest an Edit</h2>
            </div>
            <p className="text-xs text-gray-400">
              Editing info for{" "}
              <span className="font-medium text-gray-600">{restaurant.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors mt-0.5"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <AnimatePresence mode="wait">

          {/* ═══════════════════════════════════════════════════════════════
              STEP 1 — EDIT FORM
          ════════════════════════════════════════════════════════════════ */}
          {step === "edit" && (
            <motion.div
              key="edit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col flex-1 overflow-hidden"
            >
              {/* ── Section tab bar ───────────────────────────────────────── */}
              <div className="flex gap-1 px-4 py-2 border-b border-gray-100 overflow-x-auto shrink-0 scrollbar-none">
                {SECTIONS.map((s, i) => {
                  const sectionTouched = s.fields.some((f) => touched.has(f.key));
                  return (
                    <button
                      key={s.id}
                      onClick={() => setActiveSection(i)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 relative
                        ${activeSection === i
                          ? "bg-gray-900 text-white"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                      {s.icon}
                      {s.label}
                      {sectionTouched && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 absolute -top-0.5 -right-0.5" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* ── Fields ────────────────────────────────────────────────── */}
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSection.id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-4"
                  >
                    {currentSection.fields.map((field) => {
                      const val = values[field.key];
                      const isTouched = touched.has(field.key);

                      return (
                        <div key={field.key}>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                              {field.label}
                            </label>
                            {isTouched && (
                              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                                Edited
                              </span>
                            )}
                          </div>

                          {field.type === "textarea" && (
                            <textarea
                              value={String(val ?? "")}
                              onChange={(e) => setField(field.key, e.target.value)}
                              placeholder={field.placeholder}
                              rows={4}
                              className={`${inputClass} resize-none`}
                            />
                          )}

                          {field.type === "text" && (
                            <input
                              type="text"
                              value={String(val ?? "")}
                              onChange={(e) => setField(field.key, e.target.value)}
                              placeholder={field.placeholder}
                              className={inputClass}
                            />
                          )}

                          {field.type === "select" && (
                            <div className="flex gap-2 flex-wrap">
                              {field.options?.map((opt) => (
                                <button
                                  key={opt}
                                  onClick={() => setField(field.key, opt)}
                                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all
                                    ${val === opt
                                      ? "bg-gray-900 border-gray-900 text-white"
                                      : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                                    }`}
                                >
                                  {opt}
                                  {opt === "$" && <span className="ml-1 text-xs text-current opacity-60">Budget</span>}
                                  {opt === "$$" && <span className="ml-1 text-xs text-current opacity-60">Moderate</span>}
                                  {opt === "$$$" && <span className="ml-1 text-xs text-current opacity-60">Premium</span>}
                                  {opt === "$$$$" && <span className="ml-1 text-xs text-current opacity-60">Fine Dining</span>}
                                </button>
                              ))}
                            </div>
                          )}

                          {field.type === "array" && (
                            <ArrayField
                              value={Array.isArray(val) ? (val as string[]) : []}
                              onChange={(v) => setField(field.key, v)}
                              placeholder={field.placeholder}
                              suggestions={field.key === "cuisine" ? CUISINE_SUGGESTIONS : undefined}
                            />
                          )}

                          {field.hint && (
                            <p className="mt-1.5 text-[11px] text-gray-400 flex items-start gap-1">
                              <Info className="w-3 h-3 mt-0.5 shrink-0" />
                              {field.hint}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* ── Footer ────────────────────────────────────────────────── */}
              <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0">
                {/* Changes summary pill */}
                {hasChanges && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mb-3 flex items-center gap-2 text-xs text-gray-500"
                  >
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-[10px] shrink-0">
                      {changedFields.length}
                    </span>
                    {changedFields.length === 1
                      ? `1 field edited: ${fieldLabel(changedFields[0].field)}`
                      : `${changedFields.length} fields edited`}
                  </motion.div>
                )}
                <button
                  onClick={() => setStep("confirm")}
                  disabled={!hasChanges}
                  className={`w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all
                    ${hasChanges
                      ? "bg-gray-900 text-white hover:bg-gray-700 active:scale-[0.98]"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                >
                  Review Changes
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STEP 2 — CONFIRM
          ════════════════════════════════════════════════════════════════ */}
          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

                {/* Changes summary */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
                    Your Changes
                  </p>
                  <div className="space-y-2">
                    {changedFields.map(({ field, suggestedValue }) => {
                      const original = getNestedValue(
                        restaurant as unknown as Record<string, unknown>,
                        field
                      );
                      return (
                        <div
                          key={field}
                          className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
                        >
                          <p className="text-xs font-medium text-gray-500 mb-1.5">
                            {fieldLabel(field)}
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Current</p>
                              <p className="text-gray-500 line-through text-xs leading-relaxed break-words">
                                {Array.isArray(original)
                                  ? (original as string[]).join(", ") || "—"
                                  : String(original ?? "—")}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wide mb-1">Suggested</p>
                              <p className="text-gray-900 text-xs font-medium leading-relaxed break-words">
                                {Array.isArray(suggestedValue)
                                  ? (suggestedValue as string[]).join(", ") || "—"
                                  : String(suggestedValue ?? "—")}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2 block">
                    Why are you suggesting this?{" "}
                    <span className="normal-case font-normal tracking-normal">(optional)</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. The phone number changed, hours are wrong on weekends…"
                    rows={3}
                    maxLength={500}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-right text-[10px] text-gray-300 mt-1">{reason.length}/500</p>
                </div>

                <p className="text-[11px] text-gray-400 leading-relaxed flex items-start gap-1.5">
                  <Info className="w-3 h-3 mt-0.5 shrink-0 text-gray-300" />
                  Your suggestion will be reviewed by the restaurant owner or Gemio team before any changes go live.
                </p>

                {/* Error */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    >
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0 flex gap-3">
                <button
                  onClick={() => setStep("edit")}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                  ) : (
                    "Submit Suggestion"
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STEP 3 — SUCCESS
          ════════════════════════════════════════════════════════════════ */}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.22 }}
              className="flex flex-col items-center justify-center text-center px-8 py-12 flex-1"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center shadow-md mb-5"
              >
                <CheckCircle2 className="w-8 h-8 text-white" />
              </motion.div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">Thanks for your help!</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-2">
                Your{" "}
                <span className="font-semibold text-gray-700">
                  {changedFields.length} {changedFields.length === 1 ? "suggestion" : "suggestions"}
                </span>{" "}
                for <span className="font-semibold text-gray-700">{restaurant.name}</span> have been submitted.
              </p>
              <p className="text-xs text-gray-400 mb-8">
                The owner or Gemio team will review it shortly.
              </p>

              <button
                onClick={onClose}
                className="w-full py-3.5 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold transition-colors active:scale-[0.98]"
              >
                Done
              </button>
            </motion.div>
          )}

        </AnimatePresence>
      </motion.div>
    </div>
  );
}